// ── InsightsCard — 자동 인사이트 카드 (Dashboard 상단) ──
// 룰 기반 우선순위 Top N 액션 추천. 클릭하면 해당 탭으로 이동.
import React, { useState } from 'react';

const SEVERITY_STYLE = {
  urgent: {
    bg: '#fef2f2',
    border: '#fca5a5',
    icon: '#52525B',
    label: '🚨 즉시 조치',
    labelBg: '#52525B',
  },
  warning: {
    bg: '#FAFAFA',
    border: '#D4D4D8',
    icon: '#EA580C',
    label: '⚠️ 검토 필요',
    labelBg: '#EA580C',
  },
  info: {
    bg: '#FAFAFA',
    border: '#FED7AA',
    icon: '#FF6F0F',
    label: '💡 개선 기회',
    labelBg: '#FF6F0F',
  },
};

export default function InsightsCard({ insights = [], onNavigate, isMobile }) {
  const [showAll, setShowAll] = useState(false);

  if (!insights || insights.length === 0) {
    return (
      <div style={{
        background: '#FAFAFA',
        borderRadius: 16,
        padding: '16px 20px',
        marginBottom: 18,
        border: '1px solid #F4F4F5',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{ fontSize: 28 }}>✨</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#18181B' }}>
            현재 특별한 알림이 없어요
          </div>
          <div style={{ fontSize: 11, color: '#9A3412', marginTop: 2 }}>
            매장 운영이 안정적입니다. 데이터를 더 많이 입력하면 자동 인사이트가 나타나요.
          </div>
        </div>
      </div>
    );
  }

  const visible = showAll ? insights : insights.slice(0, 3);
  const hiddenCount = insights.length - 3;

  // 가장 시급한 1개 + 나머지 — 시각적 위계
  const [first, ...rest] = visible;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: isMobile ? '16px 14px' : '18px 22px',
      marginBottom: 18,
      boxShadow: '0 3px 18px rgba(0,0,0,0.08)',
    }}>
      {/* 헤더 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a' }}>
            🤖 자동 인사이트
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            매장 데이터 기반 우선순위 액션 — 클릭해서 해당 탭으로 이동
          </div>
        </div>
        <span style={{
          padding: '3px 10px', borderRadius: 12,
          background: '#f3f4f6', color: '#6b7280',
          fontSize: 11, fontWeight: 700,
        }}>
          총 {insights.length}건
        </span>
      </div>

      {/* 가장 시급한 1개 (큰 카드) */}
      {first && <BigInsight insight={first} onNavigate={onNavigate} isMobile={isMobile} />}

      {/* 나머지 */}
      {rest.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 10,
          marginTop: 10,
        }}>
          {rest.map(ins => <SmallInsight key={ins.id} insight={ins} onNavigate={onNavigate} />)}
        </div>
      )}

      {/* 더보기 / 접기 */}
      {hiddenCount > 0 && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <button onClick={() => setShowAll(s => !s)} style={{
            padding: '6px 16px', borderRadius: 8,
            border: '1px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 12, fontWeight: 700,
            cursor: 'pointer',
          }}>
            {showAll ? '▴ 접기' : `▾ ${hiddenCount}개 더 보기`}
          </button>
        </div>
      )}
    </div>
  );
}

// ── 큰 카드 (Top 1) ──
function BigInsight({ insight, onNavigate, isMobile }) {
  const s = SEVERITY_STYLE[insight.severity] || SEVERITY_STYLE.info;
  const handleClick = () => {
    if (onNavigate && insight.actionTab) onNavigate(insight.actionTab);
  };
  return (
    <div onClick={handleClick} style={{
      background: s.bg,
      border: `2px solid ${s.border}`,
      borderRadius: 12,
      padding: isMobile ? '14px 16px' : '16px 20px',
      cursor: 'pointer',
      display: 'flex', alignItems: 'flex-start', gap: 14,
      transition: 'transform 0.1s',
    }}>
      <div style={{ fontSize: 32, lineHeight: 1, flexShrink: 0 }}>{insight.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{
            padding: '2px 8px', borderRadius: 10,
            background: s.labelBg, color: '#fff',
            fontSize: 10, fontWeight: 800,
          }}>{s.label}</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1a1a' }}>{insight.title}</span>
        </div>
        <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 8 }}>
          {insight.desc}
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '5px 12px', borderRadius: 8,
          background: s.labelBg, color: '#fff',
          fontSize: 12, fontWeight: 700,
        }}>
          {insight.actionLabel} →
        </div>
      </div>
    </div>
  );
}

// ── 작은 카드 ──
function SmallInsight({ insight, onNavigate }) {
  const s = SEVERITY_STYLE[insight.severity] || SEVERITY_STYLE.info;
  const handleClick = () => {
    if (onNavigate && insight.actionTab) onNavigate(insight.actionTab);
  };
  return (
    <div onClick={handleClick} style={{
      background: s.bg,
      border: `1.5px solid ${s.border}`,
      borderRadius: 10,
      padding: '10px 12px',
      cursor: 'pointer',
      display: 'flex', alignItems: 'flex-start', gap: 10,
    }}>
      <div style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{insight.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#1a1a1a', marginBottom: 3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {insight.title}
        </div>
        <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.5, marginBottom: 5,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden' }}>
          {insight.desc}
        </div>
        <div style={{ fontSize: 11, color: s.icon, fontWeight: 700 }}>
          {insight.actionLabel} →
        </div>
      </div>
    </div>
  );
}
